import React from "react";
import PAGE from "pages";
import _ from "lodash";
import i18n, { translations, _t } from "utils/i18n";
import { matchPath } from "react-router";
import StorageIcon from "@mui/icons-material/Storage";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import FeedIcon from "@mui/icons-material/Feed";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { Property } from "csstype";
import { ERoleType } from "model/Type/ERoleType";

export interface IMenuItem {
  id: string;
  label: string;
  path?: string;
  subItems?: IMenuItem[];
  children?: IMenuItem[];
  component?: React.ComponentType<any>;
  showInMenu?: boolean;
  exact?: boolean;
  isReportDynamic?: boolean;
  icon?: JSX.Element;
  warehouses?: string[];
  maxWidth?: Property.MaxWidth;
  roles?: ERoleType[];
}

const MASTER_DATA_SUB_ITEMS: IMenuItem[] = [
  {
    label: translations.routes.mpConfiguration,
    id: "mp-configuration",
    path: "/master-data/product-configuration",
    component: PAGE.ProductConfiguration,
    showInMenu: true,
  },
  {
    label: translations.routes.productGroup,
    id: "product-group-configuration",
    path: "/master-data/product-group",
    component: PAGE.ProductGroup,
    showInMenu: true,
    roles: [ERoleType.SUPER_ADMIN, ERoleType.ADMIN, ERoleType.SUPERVISOR],
  },
  {
    label: translations.routes.warehouseConfiguration,
    id: "warehouse-configuration",
    path: "/master-data/warehouse-configuration",
    component: PAGE.WarehouseConfiguration,
    showInMenu: true,
  },
  {
    label: translations.routes.zoneConfiguration,
    id: "zone-configuration",
    path: "/master-data/zone-configuration",
    component: PAGE.ZoneConfiguration,
    showInMenu: true,
  },
  {
    label: translations.routes.palletConfiguration,
    id: "pallet-configuration",
    path: "/master-data/pallet-configuration",
    component: PAGE.PalletConfiguration,
    showInMenu: true,
  },
  {
    label: translations.routes.companyConfiguration,
    id: "customer-configuration",
    path: "/master-data/customer-configuration",
    component: PAGE.CustomerConfiguration,
    showInMenu: true,
  },
  {
    label: translations.routes.userConfiguration,
    id: "user-configuration",
    path: "/master-data/user-configuration",
    component: PAGE.UserConfiguration,
    showInMenu: true,
  },
];

const MAIN_OPERATION_SUB_ITEMS = [
  {
    label: translations.routes.productionPlan,
    id: "production-plan",
    path: "/receive/production-plan",
    component: PAGE.ProductionPlan,
    showInMenu: true,
  },
  {
    label: translations.routes.receiveDocument,
    id: "receive-document",
    path: "/receive/receive-document",
    component: PAGE.ReceiveDocument,
    showInMenu: true,
  },
  {
    label: translations.routes.loadingOrderDocument,
    id: "loading-order-document",
    path: "/receive/loading-order",
    component: PAGE.LoadingOrderDocument,
    showInMenu: true,
  },
  {
    label: translations.routes.materialRequirements,
    id: "transfer-document",
    path: "/receive/material-requirement",
    component: PAGE.MaterialRequirement,
    showInMenu: true,
  },
  {
    label: translations.routes.periodicCycleCount,
    id: "periodic-cycle-count",
    path: "/receive/periodic-cycle-count",
    component: PAGE.PeriodicCycleCount,
    showInMenu: true,
  },
];

const REPORT_SUB_ITEMS = [
  {
    label: translations.routes.warehouseDetail,
    id: "warehouse-detail-report",
    path: "/report/warehouse-detail-report",
    component: PAGE.WarehouseDetailReport,
    showInMenu: true,
  },
  {
    label: translations.routes.packagingLog,
    id: "packaging-log-report",
    path: "/report/packaging-log-report",
    component: PAGE.PackingReport,
    showInMenu: true,
  },
  {
    label: translations.routes.forkliftPerformance,
    id: "forklift-productivity-report",
    path: "/report/forklift-productivity-report",
    component: PAGE.ForkliftProductivityReport,
    showInMenu: true,
  },

  {
    label: translations.routes.deliveryDetail,
    id: "delivery-detail-report",
    path: "/report/delivery-detail-report",
    component: PAGE.DeliveryDetailReport,
    showInMenu: true,
  },
  {
    label: translations.routes.bay4s,
    id: "bay-4s-report",
    path: "/report/bay-4s-report",
    component: PAGE.Bay4SReport,
    showInMenu: true,
  },
  {
    label: translations.routes.return,
    id: "return-report",
    path: "/report/return-report",
    component: PAGE.ReturnReport,
    showInMenu: true,
  },
  {
    label: translations.routes.exportSample,
    id: "sample-export-report",
    path: "/report/sample-export-report",
    component: PAGE.ExportSampleReport,
    showInMenu: true,
  },
  {
    label: translations.routes.cycleCount,
    id: "cycle-count-report",
    path: "/report/cycle-count-report",
    component: PAGE.CycleCountReport,
    showInMenu: true,
  },
];
const QR_CODE_BANK_SUB_MENU = [
  {
    label: translations.routes.qrCodeBank,
    id: "qr-product-bank",
    path: "/bank/product",
    component: PAGE.QRCodeBank,
    showInMenu: true,
  },
];

const TRACKING_SUB_MENU = [
  {
    label: translations.routes.auditTrail,
    id: "audit-trail",
    path: "/tracking/audit-trail",
    component: PAGE.AuditTrail,
    showInMenu: true,
  },
  {
    label: translations.routes.trackingBarcode,
    id: "barcode-tracking",
    path: "/tracking/qr-code-tracking",
    component: PAGE.BarcodeTracking,
    showInMenu: true,
  },
];

export const MENU_ITEMS: IMenuItem[] = [
  {
    label: translations.routes.masterData,
    id: "master-data",
    showInMenu: true,
    icon: <StorageIcon />,
    subItems: MASTER_DATA_SUB_ITEMS,
  },
  {
    label: translations.routes.mainOperation,
    id: "main-operation",
    showInMenu: true,
    icon: <CandlestickChartIcon />,
    subItems: MAIN_OPERATION_SUB_ITEMS,
  },
  {
    label: translations.routes.tracking,
    id: "tracking",
    showInMenu: true,
    icon: <PlagiarismIcon />,
    subItems: TRACKING_SUB_MENU,
  },
  {
    label: translations.routes.bank,
    id: "bank",
    showInMenu: true,
    icon: <AccountBalanceIcon />,
    subItems: QR_CODE_BANK_SUB_MENU,
  },
  {
    label: translations.routes.reports,
    id: "report",
    showInMenu: true,
    icon: <FeedIcon />,
    subItems: REPORT_SUB_ITEMS,
  },
  {
    label: translations.routes.inProcessCheck,
    id: "inprocess-check",
    path: "/inprocess-check",
    component: PAGE.InProcessCheck,
    showInMenu: true,
    icon: <FactCheckIcon />,
  },
];

export const DETAIL_ROUTES: IMenuItem[] = [
  {
    label: "product",
    id: "add-product",
    path: "/master-data/product-configuration/new",
    component: PAGE.ProductDetail,
  },
  {
    label: "product",
    id: "product-detail",
    path: "/master-data/product-configuration/edit/:id",
    component: PAGE.ProductDetail,
  },
  {
    label: translations.routes.productGroup,
    id: "product-group-configuration",
    path: "/master-data/product-group/new",
    component: PAGE.ProductGroupDetail,
  },
  {
    label: translations.routes.productGroup,
    id: "product-group-configuration",
    path: "/master-data/product-group/:id",
    component: PAGE.ProductGroupDetail,
  },
  {
    label: "receive",
    id: "add-receive",
    path: "/receive/receive-document/new",
    component: PAGE.ReceiveDetail,
    maxWidth: "100%",
  },
  {
    label: "loading-order-detail",
    id: "loading-order-detail",
    path: "/receive/loading-order/:id&:doImportCode",
    component: PAGE.LoadingOrderDetail,
  },
  {
    label: "receive",
    id: "receive-detail",
    path: "/receive/receive-document/:stockReceivingHeaderId&:stockReceivingCode",
    component: PAGE.ReceiveDetail,
    maxWidth: "100%",
  },
  {
    label: "prepare",
    id: "prepare-detail",
    path: "/receive/prepare-document/:id&:prepareCode",
    component: PAGE.PrepareDetail,
  },
  {
    label: "delivery",
    id: "delivery-detail",
    path: "/receive/delivery-order-document/:id&:deliveryCode",
    component: PAGE.DeliveryOrderDetail,
  },
  {
    label: "product",
    id: "zone-detail",
    path: "/master-data/zone-configuration/new",
    component: PAGE.ZoneDetail,
  },

  {
    label: "product",
    id: "zone-item",
    path: "/master-data/zone-configuration/:id&:code",
    component: PAGE.ZoneDetail,
  },
  {
    label: translations.routes.addLocation,
    id: "customer-detail",
    path: "/master-data/customer-configuration/new",
    component: PAGE.CustomerDetail,
  },
  {
    label: translations.routes.addLocation,
    id: "customer-detail",
    path: "/master-data/customer-configuration/edit/:id",
    component: PAGE.CustomerDetail,
  },
  {
    label: "product",
    id: "user-detail",
    path: "/master-data/user-configuration/new",
    component: PAGE.UserDetail,
  },
  {
    label: "product",
    id: "user-detail",
    path: "/master-data/user-configuration/edit/:id",
    component: PAGE.UserDetail,
  },
  {
    label: "warehouse",
    id: "warehouse-detail",
    path: "/master-data/warehouse-configuration/new",
    component: PAGE.WarehouseDetail,
  },
  {
    label: "warehouse",
    id: "warehouse-detail",
    path: "/master-data/warehouse-configuration/edit/:id",
    component: PAGE.WarehouseDetail,
  },
  {
    label: "pallet",
    id: "pallet-detail",
    path: "/master-data/pallet-configuration/new",
    component: PAGE.PalletDetail,
  },
  {
    label: "warehouse",
    id: "pallet-detail",
    path: "/master-data/pallet-configuration/edit/:id",
    component: PAGE.PalletDetail,
  },
  {
    label: "material-requirement",
    id: "setting",
    path: "/receive/material-requirement/:id&:code",
    component: PAGE.MaterialRequirementsDetail,
    maxWidth: "100%",
  },
  {
    label: "setting",
    id: "setting",
    path: "/setting",
    component: PAGE.Setting,
  },
  {
    label: "setting",
    id: "setting",
    path: "/receive/periodic-cycle-count/new",
    component: PAGE.PeriodicCycleCountDetail,
    maxWidth: "100%",
  },
  {
    label: "setting",
    id: "setting",
    path: "/receive/periodic-cycle-count/:id",
    component: PAGE.PeriodicCycleCountDetail,
    maxWidth: "100%",
  },
  {
    label: "warehouseLayout",
    id: "warehouseLayout",
    path: "/report/warehouse-layout",
    component: PAGE.WarehouseLayout,
  },
  {
    label: "inprocess-check-detail",
    id: "inprogressCheckDetail",
    path: "/inprocess-check/new",
    component: PAGE.InProcessCheckDetail,
  },
  {
    label: "inprocess-check-detail",
    id: "inprogressCheckDetail",
    path: "/inprocess-check/:id",
    component: PAGE.InProcessCheckDetail,
  },
];

export const getLabelFromRoute = (route: string, items = MENU_ITEMS) => {
  const routes = convertMenuItems(items).find((x) => {
    return matchPath(route, {
      path: x.path,
      exact: true,
      strict: false,
    });
  });
  return i18n.t(_t(routes?.label!));
};

export const convertMenuItems = (items: IMenuItem[]) => {
  let routes: IMenuItem[] = [];
  for (const route of items) {
    if (!_.isEmpty(route.subItems)) {
      const subRoutes = convertMenuItems(route.subItems!);
      routes = routes.concat(subRoutes);
    } else {
      if (route.children) {
        const childRoutes = convertMenuItems(route.children);
        routes = routes.concat(childRoutes);
      }

      routes.push(route);
    }
  }
  return routes;
};

export const EXCLUDED_ROUTES_SCHENKER = [
  "/master-data/product-group",
  "/receive/receive-document",
  "/receive/receive-document/new",
  "/receive/receive-document/:stockReceivingHeaderId&:stockReceivingCode",
  "/receive/material-requirement",
  "/receive/material-requirement/:id&:code",
  "/receive/periodic-cycle-count",
  "/receive/periodic-cycle-count/new",
  "/receive/periodic-cycle-count/:id",
  "/bank/product",
  "/inprocess-check",
  "/inprocess-check/:id",
];

export const EXCLUDED_ROUTES_ELANCO = [
  "/receive/loading-order/:id&:doImportCode",
  "/receive/loading-order",
];
